import React, { useEffect, useRef, useState } from "react";
import { Button, Space } from "antd";
import { CheckOutlined, ClockCircleOutlined, WarningFilled } from "@ant-design/icons";

import "./ReportAdoptionPage.css";
import {
  AdoptionProgress,
  AdoptionSectionProgress,
  AdoptionStage,
} from "@/utility/adoptReportDownload";
import {
  AdoptionEstimator,
  createAdoptionEstimator,
  formatAdoptionEta,
} from "@/utility/adoptionEstimate";

/**
 * Stages that work through sections one citation at a time, versus the stages
 * that run once linking is done. The list's active row follows the first
 * group; the current-section card also names a section during styling, which
 * is document-ordered but not about linking.
 */
const LINKING_STAGES: AdoptionStage[] = ["inspect", "sections", "markers"];
const STYLING_STAGE: AdoptionStage = "styling";

export const isLinkingStage = (stage: AdoptionStage) => LINKING_STAGES.indexOf(stage) !== -1;

/**
 * The extra line under the steps counter on the idle card.
 *
 * The counter reports whole-pass operations ("51 of 254 steps"), which is a
 * real measure but does not say what Jenly is doing right this moment. This
 * names the work, matching the "4/11 citations linked" line the section card
 * shows while linking.
 *
 * `processed`/`total` are already the reporting stage's own units, so no
 * progress payload is needed. `markers` and `styling` are absent because those
 * stages render the section card instead, which names the section itself.
 */
const IDLE_DETAIL: Partial<Record<AdoptionStage, (progress: AdoptionProgress) => string>> = {
  inspect: () => "Opening your report in Word",
  // "0 of 12 sections found" would flash before the first section
  // is found — report the same "Processing…" placeholder as the step counter.
  sections: (progress) => (progress.processed === 0
    ? "Processing…"
    : `${progress.processed} of ${progress.total} sections found`),
  verify: (progress) => progress.sectionsPreserved !== undefined
    ? `Checking ${progress.sectionsTotal} report sections in Word` : `Verifying ${progress.total} citations in Word`,
  save: (progress) => progress.sectionsPreserved !== undefined
    ? "Saving your report's restored content and links" : "Saving your report's Jenly links",
};

export const adoptionIdleDetail = (progress: AdoptionProgress): string | null => {
  const describe = IDLE_DETAIL[progress.stage];
  return describe ? describe(progress) : null;
};

type SectionStatus = "done" | "active" | "pending";

interface ReportAdoptionPageProps {
  progress: AdoptionProgress;
  /** Per-section rows, pre-computed by Home so marker totals stay authoritative. */
  sections: AdoptionSectionProgress[];
  /** Stage-qualified failure text, or null while the pass is healthy. */
  error: string | null;
  onRetry: () => void;
  /** False while the pass is still in flight — Retry is for failures only. */
  canRetry: boolean;
  /** Optional platform override; defaults to detecting from Office context. */
  platform?: "web" | "desktop";
}

/**
 * Safely resolves the active Office client platform to segment throughput
 * history between Office on the Web and desktop Word (Mac / Windows).
 */
const detectOfficePlatform = (): "web" | "desktop" => {
  try {
    const office = typeof Office !== "undefined" ? Office : (globalThis as any).Office;
    const platform = office?.context?.diagnostics?.platform ?? office?.context?.platform;
    const onlineType = office?.PlatformType?.OfficeOnline;
    if (platform && onlineType && platform === onlineType) return "web";
    if (typeof platform === "string" && platform.toLowerCase().includes("online")) return "web";
    return "desktop";
  } catch (_) {
    return "desktop";
  }
};

/**
 * Full-page progress surface for the downloaded-report adoption pass.
 *
 * Sits between organization selection and MainPage in Home's page stack, so it
 * inherits the same Header/Footer chrome as every other add-in page. The
 * reference design supplies the LAYOUT (percent, subtitle, bar with a floating
 * badge); every colour, radius, and font comes from the add-in's own tokens in
 * globals.css so it reads as part of this product rather than a pasted-in
 * generic loader.
 */
function ReportAdoptionPage({ progress, sections, error, onRetry, canRetry, platform }: ReportAdoptionPageProps) {
  const total = sections.length;
  const linking = isLinkingStage(progress.stage);
  const styling = progress.stage === STYLING_STAGE;
  const rendering = progress.stage === "render";
  // Clamp against the rows we were actually given so a mismatch can never
  // render a percentage above 100% or a section index past the end of the list.
  const complete = new Set(progress.sectionsComplete.filter((index) => index >= 0 && index < total));

  // The bar tracks the WHOLE pass (inspect + section bookmarks + marker links +
  // styling + verify + save), not just linking. A section-based bar reached
  // 100% the moment the last citation was linked and then sat there through
  // every remaining stage. With no units yet, nothing has been done — so 0%.
  const unitsTotal = progress.unitsTotal;
  const unitsDone = Math.max(0, Math.min(progress.unitsDone, unitsTotal));
  const percent = unitsTotal > 0
    ? unitsDone >= unitsTotal ? 100 : Math.min(99, Math.round((unitsDone / unitsTotal) * 100)) : 0;
  const idleDetail = adoptionIdleDetail(progress);
  // While no unit has completed (the inspect stage reports progress before
  // any unit finishes), the counter says "Processing…" whether the total is
  // known or not, never "0 of 1176 steps complete".
  const awaitingFirstUnit = unitsDone === 0;
  const stepText = awaitingFirstUnit ? "Processing…" : `${unitsDone} of ${unitsTotal} steps complete`;

  // ETA from a blend of this run's throughput and the runs
  // recorded in localStorage. Purely derived from the progress prop — Home
  // does not need to know this exists.
  const estimatorRef = useRef<AdoptionEstimator | null>(null);
  if (estimatorRef.current === null) {
    estimatorRef.current = createAdoptionEstimator({ platform: platform ?? detectOfficePlatform() });
  }
  const startedAtRef = useRef<number>(Date.now());
  const completedRunRef = useRef(false);
  const [etaText, setEtaText] = useState<string | null>(null);
  useEffect(() => {
    const estimator = estimatorRef.current as AdoptionEstimator;
    const elapsedMs = Math.max(0, Date.now() - startedAtRef.current);
    estimator.observe(progress.unitsDone, elapsedMs);
    setEtaText(formatAdoptionEta(
      estimator.estimateRemainingMs(progress.unitsDone, progress.unitsTotal, elapsedMs),
    ));
    // The pass's own counts (not the clamped display value) decide when the
    // run is finished and its duration belongs in the history.
    if (!completedRunRef.current && !error && progress.unitsTotal > 0 && progress.unitsDone >= progress.unitsTotal) {
      completedRunRef.current = true;
      estimator.complete(progress.unitsTotal, Date.now() - startedAtRef.current);
    }
  }, [progress, error]);

  const inRange = (index: number | null): index is number => index !== null && index >= 0 && index < total;
  // The list row is only ever "linking" — during styling every section already
  // has its citations, so marking one active there would contradict the list.
  const activeIndex = (linking || rendering) && inRange(progress.sectionIndex) ? progress.sectionIndex : null;
  // The card additionally names the section being styled, so the user still
  // sees where the pass is while the bar moves through the tail.
  const cardIndex = (linking || styling || rendering) && inRange(progress.sectionIndex) ? progress.sectionIndex : null;
  const cardSection = cardIndex === null ? null : sections[cardIndex];

  // Completion comes from the pass's own section indices, not from a
  // positional guess: a section with no citations is credited before any
  // linking happens, so completed sections are not a prefix of the list.
  // Active is checked first so the section being worked never reads as done.
  const statusFor = (index: number): SectionStatus => {
    if (index === activeIndex) return "active";
    if (complete.has(index)) return "done";
    return "pending";
  };

  return (
    <div className="report_adoption_wrapper">
      <div className="report_adoption_panel">
        <div className="report_adoption_brand">
          <img src="/assets/brand-loader.svg" alt="Jenly" className="report_adoption_logo" />
          <div className="report_adoption_brand_name">JENLY.AI</div>
        </div>

        <div className="report_adoption_percent" role="status" aria-live="polite">
          <span className="report_adoption_percent_value">{percent}%</span>
          <span className="report_adoption_phase">{rendering ? "Rebuilding sections" : linking ? "Linking sections" : "Final checks"}</span>
          <span className="report_adoption_stage">{progress.label}</span>
          {/* The live region announces the placeholder during the
              zero-unit window, not a premature "0 of N". */}
          {awaitingFirstUnit && <span className="report_adoption_sr_only">Processing…</span>}
        </div>

        <div className="report_adoption_bar_wrap">
          <div
            className="report_adoption_bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label="Report adoption progress"
          >
            <div className="report_adoption_bar_fill" style={{ width: `${percent}%` }} />
          </div>
        </div>

        {/* Throughput-based remaining-time estimate; hidden while the
            model has no basis (cold start, no history) or once the pass fails. */}
        {!error && etaText && (
          <div className="report_adoption_eta">
            <ClockCircleOutlined className="report_adoption_eta_icon" aria-hidden="true" />
            <span>{etaText}</span>
          </div>
        )}

        {cardSection ? (
          <div className="report_adoption_current">
            <span className="report_adoption_current_index">
              Section {cardSection.index + 1} of {total}
            </span>
            <span className="report_adoption_current_name">{cardSection.name}</span>
            {linking && progress.sectionMarkersTotal > 0 && (
              <span className="report_adoption_current_markers">
                {progress.sectionMarkersDone}/{progress.sectionMarkersTotal} citations linked
              </span>
            )}
            {styling && <span className="report_adoption_current_markers">Applying Jenly&apos;s generated look</span>}
            {rendering && <span className="report_adoption_current_markers">Restoring content, formatting and citation links</span>}
            {rendering && progress.sectionMarkersTotal > 0 && (
              <span className="report_adoption_current_markers">{progress.sectionMarkersDone}/{progress.sectionMarkersTotal} citation links verified</span>
            )}
          </div>
        ) : (
          <div className="report_adoption_current report_adoption_current--idle">
            {stepText}
            {idleDetail && idleDetail !== stepText && (
              <span className="report_adoption_current_markers">{idleDetail}</span>
            )}
          </div>
        )}

        <ul className="report_adoption_list">
          {sections.map((section) => {
            const status = statusFor(section.index);
            // A done row whose section had no citation markers reads as a real
            // "needs attention" signal, not as a normal completion — paint it
            // with the orange tone in CSS instead of the green cited accent.
            const isDoneWithoutCitations = status === "done" && section.markersTotal === 0;
            return (
              <li
                key={`${section.key || "section"}-${section.index}`}
                className={`report_adoption_item report_adoption_item--${status}`}
                aria-current={status === "active" ? "true" : undefined}
              >
                <span className="report_adoption_item_icon" aria-hidden="true">
                  {status === "done" ? (
                    <CheckOutlined />
                  ) : status === "active" ? (
                    <i className="report_adoption_item_pulse" />
                  ) : null}
                </span>
                <span className="report_adoption_item_name">{section.name}</span>
                <span
                  className={`report_adoption_item_state${
                    isDoneWithoutCitations ? " report_adoption_item_state--no-citations" : ""
                  }`}
                >
                  {status === "done"
                    ? section.markersTotal > 0
                      ? `${section.markersTotal} ${section.markersTotal === 1 ? "citation" : "citations"}`
                      : "No citations"
                    : status === "active"
                    ? rendering ? "Rendering" : "Linking"
                    : "Pending"}
                </span>
              </li>
            );
          })}
        </ul>

        {error && (
          <div className="report_adoption_error" role="alert">
            <div className="report_adoption_error_head">
              <WarningFilled aria-hidden="true" />
              <span>{error}</span>
            </div>
            <p className="report_adoption_error_hint">
              The report is still readable in Word. Save the file and retry — the pass resumes where it stopped.
            </p>
            {canRetry && (
              <Button className="report_adoption_retry" onClick={onRetry}>
                <Space size={6}>
                  <img src="/assets/icon-32.png" alt="" style={{ width: "18px", height: "18px" }} />
                  <span>Retry</span>
                </Space>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportAdoptionPage;
